import { useEffect, useRef, useState } from 'react';

/**
 * 미리보기 PDF → 장별 그림(PNG) (2026-10-28). 브라우저 PDF 뷰어는 도구줄이 붙고 장을 옮길 때마다 다시 불러와 깜빡여서,
 * pdf.js 로 장마다 그림을 그려 칸에 꽉 차게 보여 준다. pdf.js 는 PPT 만들기 화면에 들어올 때만 불러온다(GRK_admin `utils/pdfPages.ts` 와 같다).
 */

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf');
let lib: Promise<PdfJs> | null = null;
const loadPdfJs = () => {
  lib ??= import('pdfjs-dist/legacy/build/pdf').then((m) => {
    m.GlobalWorkerOptions.workerPort = new Worker(new URL('pdfjs-dist/legacy/build/pdf.worker.js', import.meta.url));
    return m;
  });
  return lib;
};

export interface PdfPages {
  /** 장 순서대로 그림 주소. 아직 못 그린 장은 빈 칸 */
  pages: (string | undefined)[];
  busy: boolean;
  error: string;
}

/** blob 이 바뀌면 새로 그린다. 새 그림이 나올 때까지 앞 그림을 그대로 둔다(깜빡이지 않게) */
export function usePdfPages(blob: Blob | null, width = 1600): PdfPages {
  const [state, setState] = useState<PdfPages>({ pages: [], busy: false, error: '' });
  const live = useRef<string[]>([]);

  useEffect(() => {
    if (!blob) return;
    let stop = false;
    const made: string[] = [];
    setState((s) => ({ ...s, busy: true, error: '' }));
    (async () => {
      const pdfjs = await loadPdfJs();
      // isEvalSupported: false — CVE-2024-4367(조작된 PDF 글꼴로 임의 JS 실행, pdf.js 4.2.67 에서 수정)을 막는다. 우리 PDF 는 grk-deck 이 만들지만 그래도 끈다
      const doc = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()), isEvalSupported: false }).promise;
      try {
        for (let i = 1; i <= doc.numPages && !stop; i++) {
          const page = await doc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: width / base.width });
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('그림을 그릴 수 없는 브라우저입니다');
          await page.render({ canvasContext: ctx, viewport }).promise;
          const png = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
          if (stop || !png) break;
          const url = URL.createObjectURL(png);
          made.push(url);
          setState((s) => { const pages = [...s.pages]; pages[i - 1] = url; return { ...s, pages }; });
        }
        if (stop) return;
        const old = live.current;
        live.current = made;
        old.filter((u) => !made.includes(u)).forEach((u) => URL.revokeObjectURL(u));
        setState({ pages: made, busy: false, error: '' });
      } finally {
        doc.destroy();
      }
    })().catch((e: Error) => { if (!stop) setState((s) => ({ ...s, busy: false, error: e.message })); });
    return () => {
      stop = true;
      made.filter((u) => !live.current.includes(u)).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [blob, width]);

  useEffect(() => () => { live.current.forEach((u) => URL.revokeObjectURL(u)); }, []);
  return state;
}
